/**
 * @file Pooled dynamic lights for muzzle flashes and explosions (GDD §Fase 4).
 *
 * A fixed number of `PointLight`s is created up front and never added or
 * removed — only moved and re-tinted. That matters: changing the light count in
 * a scene forces Three.js to recompile every material's shader, which would
 * stutter on the first shot. Idle lights simply sit at zero intensity.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import { Events } from '../core/EventBus.js';

export class LightFxSystem extends System {
  static priority = 93;

  constructor() {
    super();
    /** @type {THREE.PointLight[]} @private */
    this._lights = [];
    /** @type {Float32Array | null} Remaining life per light. @private */
    this._life = null;
    /** @type {Float32Array | null} Total life per light. @private */
    this._maxLife = null;
    /** @type {Float32Array | null} Peak intensity per light. @private */
    this._peak = null;
    /** @type {number} Round-robin cursor for stealing the oldest slot. @private */
    this._cursor = 0;
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
  }

  /** @returns {void} */
  init() {
    const { scene, config } = this.ctx;
    const settings = config.lighting.dynamic;

    this._life = new Float32Array(settings.count);
    this._maxLife = new Float32Array(settings.count);
    this._peak = new Float32Array(settings.count);

    for (let i = 0; i < settings.count; i++) {
      const light = new THREE.PointLight(0xffffff, 0, settings.distance, settings.decay);
      light.castShadow = false;
      light.visible = true;
      scene.add(light);
      this._lights.push(light);
    }

    this._subscriptions.push(
      this.ctx.events.on(Events.FLASH, (payload) => this._flash(payload))
    );
  }

  /**
   * @param {{ position: THREE.Vector3, color: number, intensity: number, life: number }} payload Flash request.
   * @returns {void}
   * @private
   */
  _flash({ position, color, intensity, life }) {
    // Prefer a free slot; otherwise steal round-robin so one light source can
    // never monopolise the pool.
    let index = this._life.findIndex((remaining) => remaining <= 0);
    if (index === -1) {
      index = this._cursor;
      this._cursor = (this._cursor + 1) % this._lights.length;
    }

    const light = this._lights[index];
    light.position.copy(position);
    light.color.setHex(color);
    light.intensity = intensity;

    this._peak[index] = intensity;
    this._life[index] = life;
    this._maxLife[index] = life;
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    for (let i = 0; i < this._lights.length; i++) {
      if (this._life[i] <= 0) continue;

      this._life[i] -= dt;
      if (this._life[i] <= 0) {
        this._lights[i].intensity = 0;
        continue;
      }
      // Quadratic falloff reads as a snap rather than a fade.
      const fade = this._life[i] / this._maxLife[i];
      this._lights[i].intensity = this._peak[i] * fade * fade;
    }
  }

  /** @returns {void} */
  dispose() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
    for (const light of this._lights) {
      light.removeFromParent();
      light.dispose();
    }
    this._lights.length = 0;
  }
}
